import { observer } from "mobx-react-lite"
import { useEffect, useRef, useState } from "react"

import { Avatar, PrincipalStubAvatar } from "@/components/Avatar/Avatar.tsx"
import { Attachments } from "@/components/Chat/Attachments.tsx"
import { ContextItemPill } from "@/components/Chat/ContextItemPill.tsx"
import { DomElementScreenshot, DomElementScreenshotTooltip } from "@/components/Chat/DomElementScreenshot.tsx"
import { MessageText } from "@/components/Chat/MessageStyles.ts"
import { UserMessageModel } from "@/components/Chat/UserMessageModel.ts"
import {
  CroppedMessageText,
  ShowMore,
  StyledUserMessageBalloon,
  UserMessageAvatarWrapper,
  UserMessageContainer,
  UserMessageContext,
  UserMessageInner,
  UserMessageMyMessageAnchor,
  UserMessageOtherMessageAnchor,
  UserMessagePickerIcon,
  UserMessagePopoverAnchor
} from "@/components/Chat/UserMessageStyles.ts"
import { UserMessageTopLine } from "@/components/Chat/UserMessageTopLine.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { FigmaEmbed } from "@/components/Figma/FigmaEmbed.tsx"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { EditPromptInput } from "@/components/PromptInput/EditPromptInput.tsx"
import { useModel } from "@/hooks/useModel.ts"

import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { AiChatMessageContent, TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"


export const UserMessage = observer(({ message }: {
  message: ChatMessageModel
}) => {
  const { user } = message
  const isMyMessage = message.isMyMessage
  const editingModel = message.editingModel

  return (
    <UserMessageContainer isMyMessage={isMyMessage} isActive={message.active} data-testid="chat-message-user">
      <UserMessageInner isEditing={message.editingModel !== null}>
        <UserMessageTopLine message={message} />

        {editingModel
          ? <EditPromptInput model={editingModel.promptInputModel} cancel={() => editingModel.cancel()} />
          : <UserMessageBalloon message={message} />
        }
      </UserMessageInner>

      {!isMyMessage && <UserMessageAvatar user={user} />}
    </UserMessageContainer>
  )
})

export const UserMessageAvatar = (props: {
  user: TD_MemberProfile | null
}) => {
  return (
    <UserMessageAvatarWrapper>
      {props.user
        ? <Avatar profile={props.user} size={24} />
        : <PrincipalStubAvatar size={24} />
      }
    </UserMessageAvatarWrapper>
  )
}

const UserMessageBalloon = observer((props: {
  message: ChatMessageModel
}) => {
  const isMyMessage = props.message.isMyMessage
  const content = props.message.content as AiChatMessageContent.Text
  const model = useModel(() => new UserMessageModel(content), [content])

  const resolvedAttachments = model.resolvedAttachments.get()

  const textRef = useRef<HTMLDivElement>(null)
  const [lineCount, setLineCount] = useState<number>(0)
  const [fullHeight, setFullHeight] = useState<number>(0)
  const [lineHeight, setLineHeight] = useState<string>("21px")
  const isExpanded = model.isExpanded.get()

  useEffect(() => {
    if (textRef.current && content.text.value.length > 0) {
      const lineHeight = getComputedStyle(textRef.current).lineHeight
      const lineHeightPx = parseFloat(lineHeight)
      const height = textRef.current.scrollHeight
      const lines = Math.round(height / lineHeightPx)
      setLineCount(lines)
      setFullHeight(height)
      setLineHeight(lineHeight)
    }
  }, [content.text.value])

  const MAX_LINES = 10
  const shouldCrop = lineCount >= MAX_LINES + 2

  return (
    <StyledUserMessageBalloon isMyMessage={isMyMessage}>
      {content.text.value.length > 0 && (
        <>
          {shouldCrop ? (
            <CroppedMessageText
              maxLines={MAX_LINES}
              isExpanded={isExpanded}
              fullHeight={fullHeight}
              lineHeight={lineHeight}
            >
              <MessageText ref={textRef}>
                <MarkdownWrapper text={content.text} />
              </MessageText>
            </CroppedMessageText>
          ) : (
            <MessageText ref={textRef}>
              <MarkdownWrapper text={content.text} />
            </MessageText>
          )}
          {shouldCrop && (
            <ShowMore href="#" onClick={(e) => {
              e.preventDefault()
              model.toggleExpanded()
            }}>
              {isExpanded ? "Show less" : "Show more"}
            </ShowMore>
          )}
        </>
      )}

      {content.unfurlElements && content.unfurlElements.length > 0 && (
        content.unfurlElements.slice(0, 2).map((it) => (
          <FigmaEmbed key={it.url} url={it.url} />
        ))
      )}

      <UserMessagePopoverAnchor className={isMyMessage ? UserMessageMyMessageAnchor : UserMessageOtherMessageAnchor} />

      {(content.contextItems.length > 0 || content.attachments.length > 0 || content.domElements.length > 0) && (
        <UserMessageContext>
          <Attachments
            attachments={resolvedAttachments}
            contextItems={content.contextItems}
            domElements={content.domElements}
            domElementBuilder={(element, index) => {
              return (
                <DomElementScreenshotTooltip key={index} content={
                  <DomElementScreenshot taskId={props.message.taskId} element={element} />
                }>
                  <ContextItemPill withRemoveIcon={false}>
                    <UserMessagePickerIcon />
                    <EllipsisContainer>
                      <Ellipsis>
                        {element.name}
                      </Ellipsis>
                    </EllipsisContainer>
                  </ContextItemPill>
                </DomElementScreenshotTooltip>
              )
            }}
            showPreviewTooltips
            allowDownload
            onDownload={props.message.downloadAttachment}
          />
        </UserMessageContext>
      )}
    </StyledUserMessageBalloon>
  )
})
